import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Locker } from '@sorare/core/src/atoms/icons/Locker';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import type { Color } from '@sorare/core/src/style/types';

import { ThresholdIcon } from '../../ThresholdIcon';
import { ThresholdReward } from '../../ThresholdReward';
import { ThresholdScore } from '../../ThresholdScore';
import type {
  ThresholdRewardCollapseItem_taskCampaign,
  ThresholdRewardCollapseItem_thresholdStreakTaskThreshold,
} from './__generated__/index.graphql';

const Root = styled(Horizontal)`
  &.completed {
    opacity: 0.2;
  }
  &.locked {
    opacity: 0.5;
  }
`;
const ThresholdRewardWrapper = styled.div`
  margin-left: auto;
`;
const ThresholdLabelM = styled(LabelM)`
  .completed & {
    text-decoration: line-through;
  }
`;

type Props = {
  currentStreakToDisplay: number;
  threshold: ThresholdRewardCollapseItem_thresholdStreakTaskThreshold;
  locked?: boolean;
  completed?: boolean;
  color: Color;
  scoreTaskCampaign: ThresholdRewardCollapseItem_taskCampaign | null;
};
export const ThresholdRewardCollapseItem = ({
  currentStreakToDisplay,
  threshold,
  locked,
  completed,
  color,
  scoreTaskCampaign,
}: Props) => {
  return (
    <Root
      key={threshold.score}
      className={classNames({
        completed,
        locked,
      })}
    >
      {locked ? (
        <Locker />
      ) : (
        <ThresholdIcon currentStreakToDisplay={currentStreakToDisplay} />
      )}
      <ThresholdLabelM>
        <FormattedMessage
          id="TrckGroupPVECompetitionPrizePool.rewards"
          defaultMessage="Streak {i} Rewards"
          values={{ i: currentStreakToDisplay }}
        />
      </ThresholdLabelM>
      <ThresholdScore
        color={color}
        thresholdLowered={!!scoreTaskCampaign}
        threshold={threshold}
      />
      <ThresholdRewardWrapper>
        <ThresholdReward anyRewardConfig={threshold.rewardConfigs.at(0)} />
      </ThresholdRewardWrapper>
    </Root>
  );
};

ThresholdRewardCollapseItem.fragments = {
  taskCampaign: gql`
    fragment ThresholdRewardCollapseItem_taskCampaign on TaskCampaign {
      slug
      ...ThresholdReward_TaskCampaign
    }
    ${ThresholdReward.fragments.taskCampaign}
  ` as TypedDocumentNode<ThresholdRewardCollapseItem_taskCampaign>,
  thresholdStreakTaskThreshold: gql`
    fragment ThresholdRewardCollapseItem_thresholdStreakTaskThreshold on ThresholdStreakTaskThreshold {
      current
      score
      rewardConfigs {
        id
        ...ThresholdReward_anyRewardConfig
      }
      ...ThresholdScore_thresholdStreakTaskThreshold
    }
    ${ThresholdReward.fragments.anyRewardConfig}
    ${ThresholdScore.fragments.thresholdStreakTaskThreshold}
  ` as TypedDocumentNode<ThresholdRewardCollapseItem_thresholdStreakTaskThreshold>,
};
